import { Component, ChangeDetectorRef, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { lastValueFrom, RmsMessageService, PdfService, EmailService, CustomerCrmService, PromptService } from '@services';
import { RmsModule } from '@rms';
import { Column, TableIcon } from '@interfaces';
import { FuturesDataService, FuturesAllocation, AllocationLot, positionKey, FuturesDocument, FuturesHistory, FuturesNote, FuturesNotification, NotificationResult } from '../../../../../services/futures-data.service';
import { downloadCsv } from '../../../../../services/swap-data.service';
import { PositionRow, FND_WARNING_DAYS, fndClass, priceUnitWarning, confirmPrice } from '../futures-shared';

const CONTRACT_TEMPLATE = '/assets/pdf-templates/futures-contract.template.html';
const CONTRACT_EMAIL_TEMPLATE = '/assets/email-templates/futures-contract.html';
const DEFAULT_CONTRACT_NOTE = 'To be applied to physical coffee before First Notice Day.';

@Component({
  selector: 'app-futures-detail',
  imports: [RmsModule],
  templateUrl: './futures-detail.component.html',
  styleUrl: './futures-detail.component.scss'
})
export class FuturesDetailComponent implements OnInit, OnDestroy {
  Position!: PositionRow;
  ReserveLines: PositionRow[] = [];

  @Input({ required: true }) set position(value: PositionRow) {
    this.Position = value;
    this.resetForm();
  }

  @Input() set reserveLines(value: PositionRow[]) {
    this.ReserveLines = value?.length ? value : (this.Position ? [this.Position] : []);
  }
  WarningDays = FND_WARNING_DAYS;

  TerminalID: number | null = null;
  Terminals: { TerminalID: number; TerminalName: string }[] = [];
  ContractNote = '';
  Price: number | null = null;
  Saving = false;

  LineColumns: Column[] = [
    { key: 'FuturesLabel', label: 'Futures Month', hidden: false },
    { key: 'Contracts', label: 'Contracts', type: 'number', hidden: false },
    { key: 'WeightLbs', label: 'Weight (lbs)', type: 'number', hidden: false },
    { key: 'Price', label: 'Price', type: 'number', fixed: 4, hidden: false },
    { key: 'FND', label: 'First Notice Day', type: 'date', hidden: false, cellClass: (row) => fndClass(row.DaysToFND) },
    { key: 'PositionStatus', label: 'Position', hidden: false },
  ];

  Notes: (FuturesNote & { VisibleTo: string; FollowUp: string })[] = [];
  History: FuturesHistory[] = [];
  Documents: (FuturesDocument & { SignedCopy: string })[] = [];
  Generating = false;
  Notifications: FuturesNotification[] = [];
  Allocations: (FuturesAllocation & { TotalValue: number })[] = [];

  Lots: AllocationLot[] = [];
  LotsLoaded = false;
  LoadingLots = false;
  SelectedLot: AllocationLot | null = null;
  @ViewChild('reserveForm') reserveForm?: ElementRef<HTMLElement>;
  AllocBags: number | null = null;
  AllocPrice: number | null = null;
  AllocDiff: number | null = null;
  Allocating = false;

  LotColumns: Column[] = [
    { key: 'InventoryID', label: 'Lot #', type: 'number', hidden: false },
    { key: 'InventoryTypeName', label: 'Coffee', hidden: false },
    { key: 'WarehouseName', label: 'Warehouse', type: 'select', SearchType: 'select', hidden: false },
    { key: 'LotStatus', label: 'Status', type: 'select', SearchType: 'select', hidden: false },
    { key: 'BagWeightLbs', label: 'Bag (lbs)', type: 'number', fixed: 2, hidden: false },
    { key: 'SkuDifferential', label: 'SKU Diff', type: 'number', fixed: 4, hidden: false },
    { key: 'FreeBags', label: 'Free Bags', type: 'number', hidden: false },
    { key: 'MaxBags', label: 'Max for Contract', type: 'number', hidden: false },
  ];

  AllocationColumns: Column[] = [
    { key: 'CreatedDate', label: 'Date', type: 'date', hidden: false },
    { key: 'ReserveOrderID', label: 'Reserve #', type: 'number', hidden: false },
    { key: 'InventoryID', label: 'Lot #', type: 'number', hidden: false },
    { key: 'InventoryTypeName', label: 'Coffee', hidden: false },
    { key: 'Bags', label: 'Bags', type: 'number', hidden: false },
    { key: 'WeightLbs', label: 'Weight Applied (lbs)', type: 'number', hidden: false },
    { key: 'Basis', label: 'Futures Basis', type: 'number', fixed: 4, hidden: false },
    { key: 'Differential', label: 'Differential', type: 'number', fixed: 4, hidden: false },
    { key: 'Price', label: 'Price / lb', type: 'number', fixed: 4, hidden: false },
    { key: 'TotalValue', label: 'Total', type: 'currency', hidden: false },
    { key: 'CreatedByName', label: 'By', hidden: false },
  ];
  Notifying = false;
  Previewing = false;
  Sending = false;

  SignedDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 10);
  Today = this.SignedDate;
  SignedFile: File | null = null;
  ClearReason = '';
  SavingSignature = false;
  ViewerUrl: SafeResourceUrl | null = null;
  ViewerHref = '';
  ViewerTitle = '';
  LoadingFile = false;

  NoteTypes = ['General', 'Contract', 'Signature Follow-Up', 'Risk', 'Customer Request'];
  RelatedSteps = ['Position Created', 'Contract Generated', 'Contract Sent', 'Signature Follow-Up', 'Risk Notification', 'Other'];

  EditingNoteID: number | null = null;
  NoteType = 'General';
  IsInternal = true;
  RelatedStep = '';
  FollowUpRequired = false;
  FollowUpDate = '';
  NoteText = '';
  SavingNote = false;

  NoteColumns: Column[] = [
    { key: 'CreatedDate', label: 'Date', type: 'date', hidden: false, clickable: true, onActionKey: 'editNote', onAction: (row) => this.editNote(row) },
    { key: 'NoteType', label: 'Type', type: 'select', SearchType: 'select', hidden: false },
    { key: 'RelatedStep', label: 'Related Step', type: 'select', SearchType: 'select', hidden: false },
    { key: 'NoteText', label: 'Note', hidden: false },
    { key: 'VisibleTo', label: 'Visible To', type: 'select', SearchType: 'select', hidden: false },
    { key: 'FollowUp', label: 'Follow-Up', hidden: false },
    { key: 'CreatedByName', label: 'Created By', type: 'select', SearchType: 'select', hidden: false },
    { key: 'UpdatedByName', label: 'Edited By', hidden: true },
    { key: 'UpdatedDate', label: 'Edited', type: 'date', hidden: true },
  ];

  DocumentColumns: Column[] = [
    { key: 'VersionNumber', label: 'Version', type: 'number', hidden: false, clickable: true, onActionKey: 'viewDocument', onAction: (row) => { this.viewDocument(row); } },
    { key: 'GeneratedDate', label: 'Generated', type: 'date', hidden: false },
    { key: 'GeneratedByName', label: 'Generated By', hidden: false },
    { key: 'TimesSent', label: 'Times Sent', type: 'number', hidden: false },
    { key: 'SignedDate', label: 'Signed', type: 'date', hidden: false },
    { key: 'SignedCopy', label: 'Signed Copy', hidden: false, clickable: true, onActionKey: 'viewSignedCopy', onAction: (row) => { this.viewSignedCopy(row); } },
  ];

  NotificationColumns: Column[] = [
    { key: 'NotificationType', label: 'Type', type: 'select', SearchType: 'select', hidden: false },
    { key: 'DeliveryStatus', label: 'Status', type: 'select', SearchType: 'select', hidden: false, cellClass: (row) => 'delivery-' + String(row.DeliveryStatus).toLowerCase() },
    { key: 'Recipients', label: 'Recipients', hidden: false },
    { key: 'RetryCount', label: 'Retries', type: 'number', hidden: false },
    { key: 'CreatedDate', label: 'First Sent', type: 'date', hidden: false },
    { key: 'LastAttemptDate', label: 'Last Attempt', type: 'date', hidden: false },
    { key: 'DeliveredDate', label: 'Delivered', type: 'date', hidden: false },
    { key: 'NextRetryDate', label: 'Next Retry', type: 'date', hidden: false },
    { key: 'ErrorMessage', label: 'Error', hidden: false },
    { key: 'TriggeredByName', label: 'Triggered By', type: 'select', SearchType: 'select', hidden: false },
  ];

  HistoryColumns: Column[] = [
    { key: 'ActionDate', label: 'Date / Time', type: 'date', SearchType: 'date', hidden: false },
    { key: 'ActionType', label: 'Event', type: 'select', SearchType: 'select', hidden: false },
    { key: 'ActionDetail', label: 'Description', hidden: false },
    { key: 'FieldName', label: 'Field', type: 'select', SearchType: 'select', hidden: false },
    { key: 'BeforeValue', label: 'Before', hidden: false },
    { key: 'AfterValue', label: 'After', hidden: false },
    { key: 'ActionByName', label: 'Performed By', type: 'select', SearchType: 'select', hidden: false },
    { key: 'EntityType', label: 'Entity', type: 'select', SearchType: 'select', hidden: true },
    { key: 'ReferenceID', label: 'Reference', hidden: true },
    { key: 'SystemSource', label: 'Source', hidden: false },
    { key: 'IPAddress', label: 'IP Address', hidden: false },
  ];

  constructor(
    private futuresData: FuturesDataService,
    private pdf: PdfService,
    private sanitizer: DomSanitizer,
    private email: EmailService,
    private crm: CustomerCrmService,
    private prompt: PromptService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    await Promise.all([this.loadTerminals(), this.loadActivity()]);
  }

  private async loadTerminals() {
    try {
      const rows = (await lastValueFrom(this.futuresData.getTerminals())) ?? [];
      this.Terminals = rows
        .filter(t => t.TerminalName && t.TerminalName.trim())
        .map(t => ({ TerminalID: t.TerminalID, TerminalName: t.TerminalName!.trim() }))
        .sort((a, b) => a.TerminalName.localeCompare(b.TerminalName));
    } catch {
      this.Terminals = [];
    }
    this.cdr.markForCheck();
  }

  // free-text value saved before the dropdown existed
  get legacyTerminal(): string | null {
    return this.Position.TerminalID == null && this.Position.TerminalAllocation ? this.Position.TerminalAllocation : null;
  }

  ngOnDestroy() {
    this.closeViewer();
  }

  async loadActivity() {
    if (this.isNew) return;
    try {
      const result = await lastValueFrom(this.futuresData.getActivity(this.key));
      this.Notes = (result?.Notes ?? []).map(n => ({
        ...n,
        VisibleTo: n.IsInternal ? 'Internal Only' : 'Customer Visible',
        FollowUp: n.FollowUpRequired ? (n.FollowUpDate ? n.FollowUpDate.substring(0, 10) : 'Yes') : ''
      }));
      this.History = result?.History ?? [];
      this.Notifications = result?.Notifications ?? [];
      this.Allocations = (result?.Allocations ?? []).map(a => ({
        ...a,
        TotalValue: Math.round(Number(a.Bags) * Number(a.BagWeightLbs) * Number(a.Price) * 100) / 100
      }));
      this.Documents = (result?.Documents ?? []).map(d => ({ ...d, SignedCopy: d.SignedFilePath ? 'View' : '' }));
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load notes and audit history.');
    }
    this.cdr.markForCheck();
  }

  get unitLabel(): string {
    return this.Position.PriceUnit === 'MT' ? 'MT' : 'lb';
  }

  get key() {
    return positionKey(this.Position);
  }

  get isRms(): boolean {
    return this.Position.Source === 'RMS';
  }

  get contractWeightLbs(): number {
    return this.isRms ? Number(this.Position.OriginalWeightLbs) || 0 : Number(this.Position.WeightLbs) || 0;
  }

  get fndStatus(): string {
    return fndClass(this.Position.DaysToFND);
  }

  get isNew(): boolean {
    return this.Position.FuturesPositionID == null;
  }

  get priceChanged(): boolean {
    return this.isRms && this.Price != null && this.Price !== Number(this.Position.Price);
  }

  get priceWarning(): string | null {
    return this.isRms ? priceUnitWarning(this.Price, this.Position.PriceUnit) : null;
  }

  get isDirty(): boolean {
    return this.isNew
      || this.TerminalID !== (this.Position.TerminalID ?? null)
      || this.ContractNote.trim() !== (this.Position.ContractNote ?? '')
      || this.priceChanged;
  }

  resetForm() {
    this.TerminalID = this.Position.TerminalID ?? null;
    this.ContractNote = this.Position.ContractNote ?? (this.isNew ? DEFAULT_CONTRACT_NOTE : '');
    this.Price = this.Position.Price != null ? Number(this.Position.Price) : null;
  }

  async save() {
    if (this.Saving || !this.isDirty) return;
    if (this.isRms && this.Price != null && this.Price <= 0) {
      this.msg.error('Price must be greater than zero.');
      return;
    }
    if (this.priceChanged && this.priceWarning && !(await confirmPrice(this.prompt, this.priceWarning))) return;
    this.Saving = true;
    try {
      const result = await lastValueFrom(this.futuresData.savePosition({
        ...this.key,
        TerminalID: this.TerminalID,
        ContractNote: this.ContractNote.trim() || null,
        Price: this.isRms ? this.Price : null
      }));

      this.Position = {
        ...this.Position,
        FuturesPositionID: result.FuturesPositionID,
        OriginalWeightLbs: this.Position.OriginalWeightLbs ?? this.Position.WeightLbs,
        TerminalID: this.TerminalID,
        TerminalAllocation: this.TerminalID != null
          ? (this.Terminals.find(t => t.TerminalID === this.TerminalID)?.TerminalName ?? null)
          : this.Position.TerminalAllocation,
        ContractNote: this.ContractNote.trim() || null,
        Price: this.isRms && this.Price != null ? this.Price : this.Position.Price,
        ContractValue: this.isRms && this.Price != null ? this.Price * Number(this.Position.WeightLbs) : this.Position.ContractValue
      };
      this.resetForm();
      if (result.Notification) {
        this.showNotificationResult(result.Notification, 'Futures details saved.');
      } else {
        this.msg.success('Futures details saved.');
      }
      await this.loadActivity();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not save futures details.');
    } finally {
      this.Saving = false;
      this.cdr.markForCheck();
    }
  }

  editNote(row: FuturesNote) {
    this.EditingNoteID = row.FuturesNoteID;
    this.NoteType = row.NoteType;
    this.IsInternal = row.IsInternal;
    this.RelatedStep = row.RelatedStep ?? '';
    this.FollowUpRequired = row.FollowUpRequired;
    this.FollowUpDate = row.FollowUpDate ? row.FollowUpDate.substring(0, 10) : '';
    this.NoteText = row.NoteText;
    this.cdr.markForCheck();
  }

  clearNote() {
    this.EditingNoteID = null;
    this.NoteType = 'General';
    this.IsInternal = true;
    this.RelatedStep = '';
    this.FollowUpRequired = false;
    this.FollowUpDate = '';
    this.NoteText = '';
  }

  async saveNote() {
    if (this.SavingNote) return;
    if (!this.NoteText.trim()) {
      this.msg.error('Enter the note text.');
      return;
    }
    if (this.FollowUpRequired && !this.FollowUpDate) {
      this.msg.error('Enter a follow-up date.');
      return;
    }
    this.SavingNote = true;
    try {
      await lastValueFrom(this.futuresData.saveNote({
        ...this.key,
        FuturesNoteID: this.EditingNoteID,
        NoteType: this.NoteType,
        IsInternal: this.IsInternal,
        RelatedStep: this.RelatedStep || null,
        FollowUpRequired: this.FollowUpRequired,
        FollowUpDate: this.FollowUpRequired ? this.FollowUpDate : null,
        NoteText: this.NoteText.trim()
      }));
      this.msg.success(this.EditingNoteID ? 'Note updated.' : 'Note saved.');
      this.clearNote();
      await this.loadActivity();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not save the note.');
    } finally {
      this.SavingNote = false;
      this.cdr.markForCheck();
    }
  }

  exportHistory() {
    downloadCsv(this.History.map(h => ({
      DateTime: h.ActionDate,
      Event: h.ActionType,
      Description: h.ActionDetail,
      Field: h.FieldName,
      Before: h.BeforeValue,
      After: h.AfterValue,
      PerformedBy: h.ActionByName,
      Entity: h.EntityType,
      Reference: h.ReferenceID,
      Source: h.SystemSource,
      IPAddress: h.IPAddress
    })), `futures-audit-${this.Position.FileRef}.csv`);
  }

  get isSigned(): boolean {
    return this.Position.ContractStatusID === 5;
  }

  private contractReplacements(version: string): Record<string, string> {
    const p = this.Position;
    const date = (d: string | null) => d ? new Date(d.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US') : '—';
    const money = (v: number | null, digits: number) => v == null ? '—'
      : v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
    const unit = p.PriceUnit === 'MT' ? 'MT' : 'lb';
    // a futures agreement is for the committed amount, not what is left after reserves
    const weight = this.contractWeightLbs;
    const contracts = weight / (Number(p.ContractWeight) || 37500);
    return {
      '@@ReserveNumber@@': this.isRms ? `Contract ${p.Reference}` : p.Reference,
      '@@Version@@': version,
      '@@GeneratedDate@@': new Date().toLocaleDateString('en-US'),
      '@@Customer@@': p.Customer,
      '@@Trader@@': p.TraderName,
      '@@PurchaseDate@@': date(p.PurchaseDate),
      '@@FuturesMonth@@': p.FuturesLabel,
      '@@Exchange@@': p.ExchangeName ?? '—',
      '@@Contracts@@': contracts.toFixed(2),
      '@@Weight@@': weight.toLocaleString('en-US'),
      '@@Price@@': p.Price != null ? `${money(Number(p.Price), 4)} / ${unit}` : '—',
      '@@ContractValue@@': money(p.Price != null ? Number(p.Price) * weight : null, 2),
      '@@Terminal@@': p.TerminalAllocation ?? '—',
      '@@FND@@': date(p.FND),
      '@@ContractNote@@': p.ContractNote ?? ''
    };
  }

  private renderContract(version: string) {
    return this.pdf.GeneratePDF(CONTRACT_TEMPLATE, `Futures-Agreement-${this.Position.FileRef}-v${version}`,
      this.contractReplacements(version));
  }

  // PDFs open in an in-page popup: a new tab would background this one and html2pdf stalls there
  private showPdf(title: string, base64: string) {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    this.closeViewer();
    this.ViewerTitle = title;
    this.ViewerHref = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    this.ViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.ViewerHref);
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  closeViewer() {
    if (this.ViewerHref) URL.revokeObjectURL(this.ViewerHref);
    this.ViewerHref = '';
    this.ViewerUrl = null;
    this.ViewerTitle = '';
    this.cdr.markForCheck();
  }

  async previewContract() {
    if (this.Previewing) return;
    this.Previewing = true;
    try {
      const next = (this.Documents[0]?.VersionNumber ?? 0) + 1;
      const result = await this.renderContract(`${next} (preview)`);
      this.showPdf(`Contract preview (v${next})`, result.base64);
    } catch (err: any) {
      this.msg.error('Could not build the contract preview.');
    } finally {
      this.Previewing = false;
      this.cdr.markForCheck();
    }
  }

  async generateContract() {
    if (this.Generating || this.isSigned) return;
    this.Generating = true;
    try {
      const next = (this.Documents[0]?.VersionNumber ?? 0) + 1;
      const result = await this.renderContract(String(next));
      const saved = await lastValueFrom(this.futuresData.saveContract(this.key, result.base64));
      this.Position = { ...this.Position, ContractStatusID: 2, ContractStatusName: 'Generated' };
      this.msg.success(`Contract version ${saved.VersionNumber} generated.`);
      await this.loadActivity();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not generate the contract.');
    } finally {
      this.Generating = false;
      this.cdr.markForCheck();
    }
  }

  private async openStoredPdf(title: string, path: string) {
    if (this.LoadingFile) return;
    this.LoadingFile = true;
    this.cdr.markForCheck();
    try {
      const file = await lastValueFrom(this.futuresData.getContractFile(path));
      this.showPdf(title, file.base64);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not open the file.');
    } finally {
      this.LoadingFile = false;
      this.cdr.markForCheck();
    }
  }

  async viewDocument(doc: FuturesDocument) {
    await this.openStoredPdf(`Contract v${doc.VersionNumber}`, doc.FilePath);
  }

  // every row shows Send so the icons line up; only the latest, unsigned version is active
  documentIcons = (row: FuturesDocument): TableIcon[] => {
    const isLatest = row.FuturesContractDocumentID === this.latestDocument?.FuturesContractDocumentID;
    const canSend = isLatest && !this.isSigned && !this.Sending;
    const sendTitle = canSend
      ? (row.TimesSent ? 'Resend to customer' : 'Send to customer')
      : (this.isSigned ? 'Not available: contract is signed' : 'Not available: only the latest version can be sent');
    return [
      { icon: 'visibility', title: 'View PDF', action: (r) => { this.viewDocument(r); } },
      { icon: 'send', title: sendTitle, action: (r) => { if (canSend) this.sendContract(r); } }
    ];
  };

  get latestDocument(): FuturesDocument | null {
    return this.Documents[0] ?? null;
  }

  // confirmation / reserve-notification contacts first, else any active contact with an email
  private async contactEmails(): Promise<string[]> {
    try {
      const contacts: any[] = (await lastValueFrom(this.crm.getContacts(this.Position.CompanyID))) ?? [];
      const active = contacts.filter(c => c.IsActive !== false && c.Email);
      const flagged = active.filter(c => c.IsConfirmationRecipient || c.IsReserveNotificationRecipient);
      return [...new Set((flagged.length ? flagged : active).map(c => String(c.Email).trim()))];
    } catch {
      return [];
    }
  }

  async sendContract(row?: FuturesDocument) {
    const doc = this.latestDocument;
    if (!doc || this.Sending || this.isSigned) return;
    if (row && row.FuturesContractDocumentID !== doc.FuturesContractDocumentID) return;
    this.Sending = true;
    this.cdr.markForCheck();

    let file: { base64: string };
    let to: string[];
    try {
      [file, to] = await Promise.all([
        lastValueFrom(this.futuresData.getContractFile(doc.FilePath)),
        this.contactEmails()
      ]);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the contract file.');
      this.Sending = false;
      this.cdr.markForCheck();
      return;
    }

    const p = this.Position;
    this.email.open({
      title: doc.TimesSent ? 'Resend Futures Contract' : 'Send Futures Contract',
      defaultFrom: localStorage['email'],
      to,
      cc: [],
      subject: `Customer Futures Agreement - ${p.FuturesLabel} - ${p.Reference}`,
      templatePath: CONTRACT_EMAIL_TEMPLATE,
      templateReplacements: {
        '@@FuturesMonth@@': p.FuturesLabel,
        '@@Weight@@': this.contractWeightLbs.toLocaleString('en-US'),
        '@@ReserveNumber@@': p.Reference,
        '@@FND@@': p.FND ? new Date(p.FND.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US') : 'to be confirmed'
      },
      attachments: [{
        fileName: `Futures-Agreement-${p.FileRef}-v${doc.VersionNumber}.pdf`,
        contentType: 'application/pdf',
        contentBase64: file.base64
      }],
      requireTo: true,
      requireSubject: true,
      dearName: p.Customer
    }).subscribe(async result => {
      if (result.cancelled || !result.payload) {
        this.Sending = false;
        this.cdr.markForCheck();
        return;
      }
      const recipients = [...(result.payload.to ?? []), ...(result.payload.cc ?? [])];
      try {
        await lastValueFrom(this.futuresData.sendEmail(result.payload));
        await lastValueFrom(this.futuresData.logContractSent(positionKey(p), doc.FuturesContractDocumentID, recipients, true));
        if (p.ContractStatusID <= 2) {
          this.Position = { ...this.Position, ContractStatusID: 3, ContractStatusName: 'Sent' };
        }
        this.msg.success(`Contract version ${doc.VersionNumber} sent.`);
      } catch (err: any) {
        const message = err?.error?.error ?? 'Email failed to send.';
        try {
          await lastValueFrom(this.futuresData.logContractSent(positionKey(p), doc.FuturesContractDocumentID, recipients, false, message));
        } catch { }
        this.msg.error(message);
      } finally {
        this.Sending = false;
        await this.loadActivity();
      }
    });
  }

  onSignedFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    if (file && file.size > 15 * 1024 * 1024) {
      this.msg.error('The signed copy must be under 15 MB.');
      (event.target as HTMLInputElement).value = '';
      this.SignedFile = null;
      return;
    }
    this.SignedFile = file;
  }

  private readBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.readAsDataURL(file);
    });
  }

  async markSigned() {
    const doc = this.latestDocument;
    if (!doc || this.SavingSignature) return;
    if (!this.SignedDate || this.SignedDate > this.Today) {
      this.msg.error('Enter a signed date that is not in the future.');
      return;
    }
    this.SavingSignature = true;
    try {
      const base64 = this.SignedFile ? await this.readBase64(this.SignedFile) : null;
      await lastValueFrom(this.futuresData.markSigned(this.key, doc.FuturesContractDocumentID, this.SignedDate, base64));
      this.Position = { ...this.Position, ContractStatusID: 5, ContractStatusName: 'Signed' };
      this.SignedFile = null;
      this.msg.success(`Contract version ${doc.VersionNumber} marked as signed.`);
      await this.loadActivity();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not mark the contract as signed.');
    } finally {
      this.SavingSignature = false;
      this.cdr.markForCheck();
    }
  }

  async clearSignature() {
    if (this.SavingSignature) return;
    if (!this.ClearReason.trim()) {
      this.msg.error('Enter a reason for clearing the signature.');
      return;
    }
    this.SavingSignature = true;
    try {
      await lastValueFrom(this.futuresData.clearSignature(this.key, this.ClearReason.trim()));
      const sent = (this.latestDocument?.TimesSent ?? 0) > 0;
      this.Position = { ...this.Position, ContractStatusID: sent ? 3 : 2, ContractStatusName: sent ? 'Sent' : 'Generated' };
      this.ClearReason = '';
      this.msg.success('Signature cleared.');
      await this.loadActivity();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not clear the signature.');
    } finally {
      this.SavingSignature = false;
      this.cdr.markForCheck();
    }
  }

  async viewSignedCopy(doc: FuturesDocument) {
    if (!doc.SignedFilePath) return;
    await this.openStoredPdf(`Signed copy (v${doc.VersionNumber})`, doc.SignedFilePath);
  }

  private showNotificationResult(result: NotificationResult, savedText = '') {
    const prefix = savedText ? savedText + ' ' : '';
    const promptText = result.delivered
      ? `${prefix}Risk Manager notified (${result.recipients}).`
      : result.pending
        ? `${prefix}Risk Management will be notified once a Risk Manager is set up. It is saved as Pending on the Risk Notification tab.`
        : `${prefix}The Risk Manager notification has not gone out yet: ${result.error} `
          + 'It is logged and can be resent from the Risk Notification tab.';
    this.prompt.open({
      promptText,
      buttons: [{ label: 'OK', value: true, buttonClass: 'btn-bg-blue' }]
    });
  }

  get riskNotification(): FuturesNotification | null {
    return this.Notifications[0] ?? null;
  }

  async notifyRisk() {
    if (this.Notifying || this.isNew) return;
    this.Notifying = true;
    try {
      const result = await lastValueFrom(this.futuresData.notifyRisk(this.key));
      this.showNotificationResult(result.Notification);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not send the notification.');
    } finally {
      this.Notifying = false;
      await this.loadActivity();
    }
  }

  get remainingLbs(): number {
    return Number(this.Position.WeightLbs) || 0;
  }

  get bagsWeight(): number {
    return this.SelectedLot && this.AllocBags ? this.AllocBags * Number(this.SelectedLot.BagWeightLbs) : 0;
  }

  // the last bag can round up past the remaining weight; Royal absorbs the difference
  get allocWeight(): number {
    return Math.min(this.bagsWeight, this.remainingLbs);
  }

  get absorbedLbs(): number {
    return Math.max(0, this.bagsWeight - this.remainingLbs);
  }

  get canApply(): boolean {
    return !!this.SelectedLot && !!this.AllocBags && this.AllocBags > 0
      && this.AllocBags <= this.SelectedLot.MaxBags
      && this.AllocPrice != null && String(this.AllocPrice) !== '' && Number(this.AllocPrice) >= 0
      && !this.Allocating;
  }

  async loadLots() {
    if (!this.Position.FuturesPositionID || this.LoadingLots) return;
    this.LoadingLots = true;
    this.cdr.markForCheck();
    try {
      this.Lots = (await lastValueFrom(this.futuresData.getAllocationLots(this.Position.FuturesPositionID))) ?? [];
      this.LotsLoaded = true;
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load inventory lots.');
    } finally {
      this.LoadingLots = false;
      this.cdr.markForCheck();
    }
  }

  selectLot(lot: AllocationLot) {
    this.SelectedLot = lot;
    this.AllocBags = null;
    this.AllocDiff = lot.SkuDifferential != null ? Number(lot.SkuDifferential) : null;
    this.applyDifferential();
    this.cdr.detectChanges();
    const form = this.reserveForm?.nativeElement;
    form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    form?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
  }

  // contract futures price per lb; MT contracts are converted
  get basisPerLb(): number | null {
    if (this.Position.Price == null) return null;
    const price = Number(this.Position.Price);
    return this.Position.PriceUnit === 'MT' ? price / 2204.62262 : price;
  }

  get calculatedPrice(): number | null {
    if (this.basisPerLb == null || this.AllocDiff == null || String(this.AllocDiff) === '') return null;
    return Math.round((this.basisPerLb + Number(this.AllocDiff)) * 10000) / 10000;
  }

  get priceOverridden(): boolean {
    return this.calculatedPrice != null && this.AllocPrice != null && Number(this.AllocPrice) !== this.calculatedPrice;
  }

  applyDifferential() {
    this.AllocPrice = this.calculatedPrice;
  }

  async applyToReserve() {
    if (!this.canApply || !this.Position.FuturesPositionID) return;
    const lot = this.SelectedLot!;
    this.Allocating = true;
    try {
      const result = await lastValueFrom(this.futuresData.allocateToReserve(
        this.Position.FuturesPositionID, lot.InventoryID, Number(this.AllocBags), Number(this.AllocPrice),
        this.AllocDiff != null && String(this.AllocDiff) !== '' ? Number(this.AllocDiff) : null));

      const remaining = Number(result.RemainingWeightLbs);
      const contractWeight = Number(this.Position.ContractWeight) || 37500;
      this.Position = {
        ...this.Position,
        WeightLbs: remaining,
        AllocatedWeightLbs: Number(this.Position.AllocatedWeightLbs) + Number(result.WeightLbs),
        Contracts: remaining / contractWeight,
        IsActive: remaining > 0,
        PositionStatus: remaining > 0 ? 'Partial' : 'Closed',
        ContractValue: this.Position.Price != null ? Number(this.Position.Price) * remaining : null
      };

      this.prompt.open({
        promptText: `${this.AllocBags} bags of ${lot.InventoryTypeName} (lot ${lot.InventoryID}) reserved as reserve ${result.ReserveOrderID}. `
          + `${Number(result.WeightLbs).toLocaleString('en-US')} lbs applied, ${remaining.toLocaleString('en-US')} lbs remaining on the contract.`,
        buttons: [{ label: 'OK', value: true, buttonClass: 'btn-bg-blue' }]
      });

      this.SelectedLot = null;
      this.AllocBags = null;
      this.AllocPrice = null;
      this.AllocDiff = null;
      await Promise.all([this.loadActivity(), remaining > 0 ? this.loadLots() : Promise.resolve()]);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not create the reserve.');
    } finally {
      this.Allocating = false;
      this.cdr.markForCheck();
    }
  }
}
